import type { RepeatSlot } from '@fieldops/shared'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { useJobHref } from '../../lib/job-overlay.js'
import { Icon } from '../Icon.js'
import { dayLabel, monthLabel } from './rule.js'

export type TrackRow = {
  id: string
  name: string
  sub: string
  slots: readonly RepeatSlot[]
  /** Right of the months: the next visit on the agreement page. */
  aside?: ReactNode
  /** Its visits still to come are drawn as paused rather than planned. */
  paused?: boolean
  /** The name opens the row's editor. Drawn only in the `dots` look. */
  onEdit?: () => void
  editLabel?: string
  /** A tick before the name, for the agreement page's bulk edits. Drawn only in the `dots` look. */
  tick?: ReactNode
  /** Badges after the sub, such as Overdue or No price. Drawn only in the `dots` look. */
  flags?: ReactNode
  /** Its visits that go on one job with others, by sequence. Drawn only in the `dots` look. */
  joined?: ReadonlyMap<number, Joined>
  /** A visit with no job opens this, rather than being a plain mark. Drawn only in the `dots` look. */
  onSlot?: (slot: RepeatSlot) => void
}

/** A visit made as one job with others: the job's key, and what else goes on it. */
export type Joined = { key: string; others: readonly string[] }

/**
 * A heading and its rows. In the `dots` look a group may be `folded`: one
 * line, the heading in the first column, every row's visits merged onto
 * the months and `aside` at the end, so a council of 168 sites is 168 lines.
 */
export type TrackGroup = {
  key: string
  heading?: ReactNode
  rows: readonly TrackRow[]
  folded?: boolean
  /** The folded line's name, for its row. */
  label?: string
  aside?: ReactNode
}

/**
 * `track` joins a row's visits with a line, solid behind and dashed ahead —
 * the job card's Schedule tab. `dots` is the agreement page: loose marks, a
 * solid blue for a job already on the schedule and a dashed ring for planned.
 */
export type TrackLook = 'track' | 'dots'

export type Mark = 'done' | 'current' | 'booked' | 'planned' | 'overdue' | 'skipped' | 'paused'

export const MARK_LABEL: Record<Mark, string> = {
  done: 'Done',
  current: 'This visit',
  booked: 'On the schedule',
  planned: 'Planned',
  overdue: 'Overdue',
  skipped: 'Skipped',
  paused: 'Paused',
}

export function markOf(slot: RepeatSlot, currentJobId: string | null, paused: boolean): Mark {
  if (currentJobId && slot.job?.id === currentJobId) return 'current'
  if (paused && !slot.job && (slot.state === 'NEXT' || slot.state === 'PLANNED' || slot.state === 'OVERDUE')) {
    return 'paused'
  }
  switch (slot.state) {
    case 'DONE':
      return 'done'
    case 'CREATED':
      return 'booked'
    case 'SKIPPED':
    case 'UNSUCCESSFUL':
      return 'skipped'
    case 'OVERDUE':
      return 'overdue'
    default:
      return 'planned'
  }
}

/** A visit that is behind the series: the solid, green part of its line. */
const settled = (mark: Mark) => mark === 'done' || mark === 'skipped'

/** The mark a folded line's count takes: the one that most needs seeing. */
const LOUDEST: readonly Mark[] = ['overdue', 'current', 'booked', 'planned', 'paused', 'done', 'skipped']

/** A month on a folded line with more visits than fit: how many, in the loudest mark's colour. */
export function SlotCount({ marks }: { marks: readonly Mark[] }) {
  const loudest = LOUDEST.find((mark) => marks.includes(mark)) ?? 'planned'
  return (
    <span
      className={cn(
        'inline-block min-w-[18px] rounded-full px-1 text-center text-[10.5px] leading-[16px] font-semibold tabular-nums',
        loudest === 'overdue'
          ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-300 ring-inset'
          : loudest === 'booked' || loudest === 'current'
            ? 'bg-blue-600 text-white'
            : loudest === 'done'
              ? 'bg-green-600 text-white'
              : loudest === 'planned'
                ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 ring-inset'
                : 'bg-ink-100 text-ink-500',
      )}
    >
      {marks.length}
    </span>
  )
}

export function SlotDot({ mark, small = false, look = 'track' }: { mark: Mark; small?: boolean; look?: TrackLook }) {
  const size = small ? 'size-2.5' : undefined
  if (look === 'dots') {
    const dot = cn('rounded-full', size ?? 'size-3.5')
    switch (mark) {
      case 'done':
        return <span className={cn(dot, 'bg-green-600 ring-2 ring-green-100')} />
      case 'current':
      case 'booked':
        return <span className={cn(dot, 'bg-blue-600 ring-2 ring-blue-100')} />
      case 'overdue':
        return <span className={cn(dot, 'border-[1.5px] border-dashed border-amber-500 bg-amber-50')} />
      case 'skipped':
        return <span className={cn('rounded-full bg-ink-200', size ?? 'size-2.5')} />
      case 'paused':
        return <span className={cn(dot, 'border-[1.5px] border-dotted border-ink-400 bg-white')} />
      default:
        return <span className={cn(dot, 'border-[1.5px] border-dashed border-blue-400 bg-white')} />
    }
  }
  switch (mark) {
    case 'done':
      return (
        <span className={cn('grid place-items-center rounded-full bg-green-600 text-white', size ?? 'size-5')}>
          {small ? null : <Icon name="check" className="size-3" strokeWidth={3} />}
        </span>
      )
    case 'current':
      return <span className={cn('rounded-full bg-blue-600 ring-4 ring-blue-100', size ?? 'size-5')} />
    case 'booked':
      return <span className={cn('rounded-full border-2 border-blue-500 bg-blue-50', size ?? 'size-4')} />
    case 'overdue':
      return <span className={cn('rounded-full border-2 border-amber-500 bg-amber-50', size ?? 'size-4')} />
    case 'skipped':
      return <span className={cn('rounded-full bg-ink-200', size ?? 'size-3.5')} />
    default:
      return <span className={cn('rounded-full border-2 border-ink-300 bg-white', size ?? 'size-3.5')} />
  }
}

/**
 * Services across the months of a term: one row each, a dot for every visit
 * in its month, joined by a line that is solid while it is behind and dashed
 * ahead. Today's month is a red band down every row. Decision 96.
 *
 * The slots are the server's `repeatSlots`, the same ones the job card's
 * timeline draws. A dot with a job opens it.
 */
export function ServiceTracks({
  months,
  today,
  groups,
  currentJobId = null,
  upcoming = null,
  label,
  showAside = false,
  asideLabel = 'Next',
  showKey = true,
  look = 'track',
  firstLabel = 'Service',
  footer = null,
}: {
  months: readonly string[]
  today: string
  groups: readonly TrackGroup[]
  /** The job the card is open on; its visit is "This visit". */
  currentJobId?: string | null
  /** The visit to act on next, ringed in green. */
  upcoming?: { rowId: string; sequence: number } | null
  label: string
  showAside?: boolean
  asideLabel?: string
  showKey?: boolean
  look?: TrackLook
  firstLabel?: string
  /** A last row across the whole grid, such as Add site. */
  footer?: ReactNode
}) {
  const jobHref = useJobHref()
  const dots = look === 'dots'
  const n = months.length
  const nowMonth = today.slice(0, 7)
  const years = new Set(months.map((month) => month.slice(0, 4)))
  const columns = dots
    ? `minmax(300px, 4.2fr) repeat(${n}, minmax(30px, 1fr))${showAside ? ' 72px' : ''}`
    : `minmax(170px, 1.7fr) repeat(${n}, minmax(34px, 1fr))${showAside ? ' 72px' : ''}`
  const anyPaused = groups.some((group) => group.rows.some((row) => row.paused))
  const joins = groups.map((group) => joinsIn(group.rows, months))

  const band = (month: string) => month === nowMonth && 'bg-red-50/80 shadow-[inset_1px_0_0_var(--color-red-200),inset_-1px_0_0_var(--color-red-200)]'

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto">
        <div role="table" aria-label={label} className="grid min-w-[640px]" style={{ gridTemplateColumns: columns }}>
          <div role="row" className="contents">
            <span
              role="columnheader"
              className={cn(
                'px-1 pb-2 text-[10px] font-bold tracking-[0.06em] text-ink-400 uppercase',
                dots && 'self-end border-b border-ink-100',
              )}
            >
              {firstLabel}
            </span>
            {months.map((month, index) => {
              const year =
                years.size > 1 && (index === 0 || month.endsWith('-01')) ? (
                  <span className={cn('text-[9.5px]', dots ? 'text-ink-300' : 'text-ink-400')}>{month.slice(0, 4)}</span>
                ) : null
              return (
                <span
                  key={month}
                  role="columnheader"
                  className={cn(
                    'flex flex-col items-center justify-end pb-2 text-[11px] leading-tight',
                    month === nowMonth ? 'font-bold text-red-600' : 'text-ink-400',
                    dots && 'border-b border-ink-100',
                    band(month),
                  )}
                >
                  {dots ? null : year}
                  {monthLabel(month)}
                  {dots ? year : null}
                  {month === nowMonth ? <span className="text-[8.5px] tracking-wide uppercase">Today</span> : null}
                </span>
              )
            })}
            {showAside ? (
              <span
                role="columnheader"
                className={cn(
                  'pb-2 text-right text-[10px] font-bold tracking-[0.06em] text-ink-400 uppercase',
                  dots && 'self-end border-b border-ink-100',
                )}
              >
                {asideLabel}
              </span>
            ) : null}
          </div>

          {groups.map((group, groupIndex) =>
            dots && group.folded ? (
              <div key={group.key} role="rowgroup" className="contents">
                <FoldedLine
                  group={group}
                  months={months}
                  currentJobId={currentJobId}
                  ruled={groupIndex > 0}
                  band={band}
                  showAside={showAside}
                />
              </div>
            ) : (
              <div key={group.key} role="rowgroup" className="contents">
                {group.heading ? (
                  <div role="row" className="contents">
                    <div
                      role="rowheader"
                      className={cn(
                        'px-1',
                        dots ? cn('pt-4 pb-1.5', groupIndex > 0 && 'border-t border-ink-100') : 'border-t border-ink-100 pt-3 pb-1',
                      )}
                      style={{ gridColumn: '1 / -1' }}
                    >
                      {group.heading}
                    </div>
                  </div>
                ) : null}
                {group.rows.map((row, rowIndex) => {
                  const placed = row.slots
                    .map((slot) => ({
                      slot,
                      at: months.indexOf(slot.dueOn.slice(0, 7)),
                      mark: markOf(slot, currentJobId, row.paused ?? false),
                    }))
                    .filter((entry) => entry.at >= 0)
                  const first = placed[0]?.at ?? -1
                  const last = placed.at(-1)?.at ?? -1
                  const lastSettled = [...placed].reverse().find((entry) => settled(entry.mark))?.at ?? first
                  const x = (at: number) => `${((at + 0.5) / n) * 100}%`
                  const rule = dots ? cn(rowIndex === group.rows.length - 1 && 'pb-2') : 'border-t border-ink-100'

                  return (
                    <div key={row.id} role="row" aria-label={row.name} className="contents">
                      {dots && row.onEdit ? (
                        <div role="rowheader" className={cn('flex min-w-0 items-center gap-1 py-1 pr-1 pl-2.5', rule)}>
                          {row.tick}
                          <button
                            type="button"
                            onClick={row.onEdit}
                            aria-label={row.editLabel ?? `Edit ${row.name}`}
                            className="group/edit flex min-w-0 flex-1 items-baseline gap-2 rounded-md px-1.5 py-0.5 text-left hover:bg-ink-50"
                          >
                            <span className="max-w-full shrink-0 truncate text-[13px] font-medium text-ink-900 group-hover/edit:text-brand-700">
                              {row.name}
                            </span>
                            <span className="min-w-0 truncate text-[11.5px] text-ink-400">{row.sub}</span>
                            {row.flags ? <span className="flex shrink-0 items-center gap-1 self-center">{row.flags}</span> : null}
                            <Icon
                              name="pencil"
                              className="ml-auto size-3.5 shrink-0 self-center text-ink-400 opacity-0 group-hover/edit:opacity-100 group-focus-visible/edit:opacity-100"
                            />
                          </button>
                        </div>
                      ) : dots ? (
                        <div role="rowheader" className={cn('flex min-w-0 items-baseline gap-2 py-1.5 pr-1 pl-4', rule)}>
                          {row.tick}
                          <span className="max-w-full shrink-0 truncate text-[13px] font-medium text-ink-900">{row.name}</span>
                          <span className="min-w-0 truncate text-[11.5px] text-ink-400">{row.sub}</span>
                          {row.flags ? <span className="ml-auto flex shrink-0 items-center gap-1 self-center">{row.flags}</span> : null}
                        </div>
                      ) : (
                        <div role="rowheader" className="min-w-0 border-t border-ink-100 px-1 py-2.5">
                          <span className="block truncate text-[13px] font-semibold text-ink-900">{row.name}</span>
                          <span className="block text-[11.5px] leading-snug text-ink-500">{row.sub}</span>
                        </div>
                      )}
                      <div className={cn('relative grid', rule)} style={{ gridColumn: `2 / span ${n}`, gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
                        {!dots && first >= 0 && last > first ? (
                          <>
                            {lastSettled > first ? (
                              <span
                                aria-hidden="true"
                                className="absolute top-1/2 h-0.5 -translate-y-1/2 bg-green-500"
                                style={{ left: x(first), width: `${((lastSettled - first) / n) * 100}%` }}
                              />
                            ) : null}
                            {last > lastSettled ? (
                              <span
                                aria-hidden="true"
                                className="absolute top-1/2 -translate-y-1/2 border-t-2 border-dashed border-ink-300"
                                style={{ left: x(lastSettled), width: `${((last - lastSettled) / n) * 100}%` }}
                              />
                            ) : null}
                          </>
                        ) : null}
                        {months.map((month, index) => {
                          const here = placed.filter((entry) => entry.at === index)
                          const link = dots ? joins[groupIndex]!.link(rowIndex, index) : null
                          return (
                            <div key={month} role="cell" className={cn('relative flex items-center justify-center gap-0.5', dots ? 'py-1.5' : 'py-2', band(month))}>
                              {link?.up ? <span aria-hidden="true" className="absolute top-0 left-1/2 h-1/2 w-0.5 -translate-x-1/2 bg-blue-300" /> : null}
                              {link?.down ? <span aria-hidden="true" className="absolute top-1/2 left-1/2 h-1/2 w-0.5 -translate-x-1/2 bg-blue-300" /> : null}
                              {here.map(({ slot, mark }) => {
                                const joined = dots ? row.joined?.get(slot.sequence) : undefined
                                const title = [
                                  `${MARK_LABEL[mark]} · ${dayLabel(slot.dueOn)}`,
                                  joined ? `one job with ${joined.others.join(', ')}` : null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')
                                const ringed = upcoming?.rowId === row.id && upcoming.sequence === slot.sequence
                                const dot = (
                                  <span
                                    className={cn(
                                      'grid place-items-center',
                                      ringed ? 'rounded-lg bg-white p-1 ring-2 ring-green-500' : 'bg-white/0',
                                    )}
                                  >
                                    <SlotDot mark={mark} small={here.length > 2} look={look} />
                                    {joined && !joins[groupIndex]!.drawn(joined.key, index) ? (
                                      <span
                                        aria-hidden="true"
                                        className="absolute -top-1.5 -right-2.5 text-[9px] leading-none font-bold text-blue-700"
                                      >
                                        {`+${joined.others.length}`}
                                      </span>
                                    ) : null}
                                  </span>
                                )
                                if (!slot.job && dots && row.onSlot) {
                                  return (
                                    <button
                                      key={slot.sequence}
                                      type="button"
                                      title={title}
                                      aria-label={title}
                                      onClick={() => row.onSlot!(slot)}
                                      className="relative rounded-full hover:ring-2 hover:ring-ink-200 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:outline-none"
                                    >
                                      {dot}
                                    </button>
                                  )
                                }
                                return slot.job && mark !== 'current' ? (
                                  <Link
                                    key={slot.sequence}
                                    to={jobHref(slot.job.id)}
                                    title={title}
                                    aria-label={title}
                                    className="relative rounded-full hover:ring-2 hover:ring-ink-200"
                                  >
                                    {dot}
                                  </Link>
                                ) : (
                                  <span key={slot.sequence} role="img" title={title} aria-label={title} className="relative">
                                    {dot}
                                  </span>
                                )
                              })}
                            </div>
                          )
                        })}
                      </div>
                      {showAside ? (
                        <div
                          role="cell"
                          className={cn(
                            'flex items-center justify-end text-[12.5px] whitespace-nowrap tabular-nums',
                            dots ? cn('font-semibold text-ink-900', rule) : 'border-t border-ink-100 text-ink-700',
                          )}
                        >
                          {row.aside}
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            ),
          )}

          {footer ? (
            <div role="row" className="contents">
              <div role="cell" className="border-t border-ink-100 pt-2" style={{ gridColumn: '1 / -1' }}>
                {footer}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {showKey ? (
        <SlotKey
          look={look}
          marks={['done', ...(currentJobId ? ['current' as const] : []), 'booked', 'planned', anyPaused ? 'paused' : 'skipped']}
        />
      ) : null}
    </div>
  )
}

/**
 * A folded group: its heading, then each month with every row's visits in
 * it — a dot each for one or two, a count for more — then its aside.
 */
function FoldedLine({
  group,
  months,
  currentJobId,
  ruled,
  band,
  showAside,
}: {
  group: TrackGroup
  months: readonly string[]
  currentJobId: string | null
  ruled: boolean
  band: (month: string) => string | false
  showAside: boolean
}) {
  const n = months.length
  const rule = cn('py-1.5', ruled && 'border-t border-ink-100')
  const placed = group.rows.flatMap((row) =>
    row.slots.map((slot) => ({
      row,
      slot,
      month: slot.dueOn.slice(0, 7),
      mark: markOf(slot, currentJobId, row.paused ?? false),
    })),
  )

  return (
    <div role="row" aria-label={group.label} className="contents">
      <div role="rowheader" className={cn('flex min-w-0 items-center pr-1 pl-1', rule)}>
        {group.heading}
      </div>
      <div className={cn('grid', ruled && 'border-t border-ink-100')} style={{ gridColumn: `2 / span ${n}`, gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
        {months.map((month) => {
          const here = placed.filter((entry) => entry.month === month)
          const labelOf = (entry: (typeof here)[number]) =>
            `${entry.row.name} · ${MARK_LABEL[entry.mark]} · ${dayLabel(entry.slot.dueOn)}`
          const title = here.map(labelOf).join('\n')
          // Visits on one job sit together in one pill; the rest stand alone.
          const runs: (typeof here)[] = []
          for (const entry of here) {
            const key = entry.row.joined?.get(entry.slot.sequence)?.key
            const run = key ? runs.find((candidate) => candidate[0]!.row.joined?.get(candidate[0]!.slot.sequence)?.key === key) : undefined
            if (run) run.push(entry)
            else runs.push([entry])
          }
          const mark = (entry: (typeof here)[number]) => {
            const label = labelOf(entry)
            const dot = <SlotDot mark={entry.mark} small={here.length > 1} look="dots" />
            return !entry.slot.job && entry.row.onSlot ? (
              <button
                key={`${entry.row.id}:${entry.slot.sequence}`}
                type="button"
                title={title}
                aria-label={label}
                onClick={() => entry.row.onSlot!(entry.slot)}
                className="grid place-items-center rounded-full hover:ring-2 hover:ring-ink-200 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:outline-none"
              >
                {dot}
              </button>
            ) : (
              <span key={`${entry.row.id}:${entry.slot.sequence}`} role="img" title={title} aria-label={label} className="grid place-items-center">
                {dot}
              </span>
            )
          }
          return (
            <div key={month} role="cell" className={cn('flex items-center justify-center gap-0.5 py-1.5', band(month))}>
              {here.length > 2 ? (
                <span
                  role="img"
                  title={title}
                  aria-label={
                    runs.length < here.length
                      ? `${here.length} visits in ${monthLabel(month)}, as ${runs.length} job${runs.length === 1 ? '' : 's'}`
                      : `${here.length} visits in ${monthLabel(month)}`
                  }
                  className="grid place-items-center"
                >
                  <SlotCount marks={here.map((entry) => entry.mark)} />
                </span>
              ) : (
                runs.map((run) =>
                  run.length > 1 ? (
                    <JoinedPill key={`${run[0]!.row.id}:${run[0]!.slot.sequence}`} label={`One job: ${run.map((entry) => entry.row.name).join(' + ')}`}>
                      {run.map(mark)}
                    </JoinedPill>
                  ) : (
                    mark(run[0]!)
                  ),
                )
              )}
            </div>
          )
        })}
      </div>
      {showAside ? (
        <div role="cell" className={cn('flex items-center justify-end text-[12.5px] font-semibold whitespace-nowrap text-ink-900 tabular-nums', rule)}>
          {group.aside}
        </div>
      ) : null}
    </div>
  )
}

/** Visits made as one job, drawn together. */
export function JoinedPill({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <span role={label ? 'group' : undefined} aria-label={label} className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-0.5 py-px ring-1 ring-blue-300 ring-inset">
      {children}
    </span>
  )
}

/**
 * Where a group's rows have visits on one job in the same month: a line runs
 * down that month from the first such row to the last, through any between.
 * `drawn` says whether a visit's partners are in this group at all — By
 * service, they are other groups' rows, so the dot says how many instead.
 */
function joinsIn(rows: readonly TrackRow[], months: readonly string[]) {
  const spans = new Map<string, { at: number; rows: number[] }>()
  rows.forEach((row, index) => {
    for (const slot of row.slots) {
      const joined = row.joined?.get(slot.sequence)
      const at = months.indexOf(slot.dueOn.slice(0, 7))
      if (!joined || at < 0) continue
      const id = `${joined.key}|${at}`
      const span = spans.get(id) ?? { at, rows: [] }
      if (!span.rows.includes(index)) span.rows.push(index)
      spans.set(id, span)
    }
  })
  const linked = [...spans.values()]
    .filter((span) => span.rows.length > 1)
    .map((span) => ({ at: span.at, first: Math.min(...span.rows), last: Math.max(...span.rows) }))

  return {
    drawn: (key: string, at: number) => (spans.get(`${key}|${at}`)?.rows.length ?? 0) > 1,
    link: (row: number, at: number) => ({
      up: linked.some((span) => span.at === at && row > span.first && row <= span.last),
      down: linked.some((span) => span.at === at && row >= span.first && row < span.last),
    }),
  }
}

/** What each mark means, in the look it is drawn in. */
export function SlotKey({ marks, look = 'track', className }: { marks: readonly Mark[]; look?: TrackLook; className?: string }) {
  return (
    <ul aria-label="Key" className={cn('flex flex-wrap items-center gap-4 text-[12px] text-ink-600', className)}>
      {marks.map((mark) => (
        <li key={mark} className="flex items-center gap-1.5">
          <span aria-hidden="true" className={cn('grid place-items-center', look === 'dots' ? 'w-4' : 'w-5')}>
            <SlotDot mark={mark} small={false} look={look} />
          </span>
          {MARK_LABEL[mark]}
        </li>
      ))}
    </ul>
  )
}
